import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, CartAddEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    if (this.dataset.productUrl) {
      const directUrl = new URL(this.dataset.productUrl, window.location.origin);
      if (this.dataset.cartEditVariant) directUrl.searchParams.set('variant', this.dataset.cartEditVariant);
      return directUrl.toString();
    }

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const essentialCard = this.closest('.rp-mle-card');
    const essentialCardLink = essentialCard?.querySelector('.rp-mle-card__media-link');
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink() || essentialCardLink;

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const dialogComponent = document.getElementById('quick-add-dialog');
    const dialog = dialogComponent?.querySelector('.quick-add-modal');
    if (dialogComponent instanceof HTMLElement && dialog instanceof HTMLElement) {
      const isCartEdit = Boolean(this.dataset.cartEditKey);
      dialogComponent.dataset.cartEditKey = this.dataset.cartEditKey || '';
      dialogComponent.dataset.cartEditVariant = this.dataset.cartEditVariant || '';
      dialogComponent.dataset.cartEditQuantity = this.dataset.cartEditQuantity || '1';
      dialogComponent.dataset.cartEditProperties = this.dataset.cartEditProperties || '{}';
      dialog.dataset.cartEdit = isCartEdit ? 'true' : 'false';
    }

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          let productDescription = '';
          for (const script of html.querySelectorAll('script[type="application/ld+json"]')) {
            try {
              const data = JSON.parse(script.textContent || '{}');
              const entries = Array.isArray(data) ? data : data['@graph'] || [data];
              const productEntry = entries.find((entry) => entry?.['@type'] === 'Product');
              if (productEntry?.description) {
                productDescription = String(productEntry.description).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                break;
              }
            } catch (_) {
              // Ignore unrelated or malformed structured-data blocks.
            }
          }
          const metaDescription = html.querySelector('meta[name="description"]')?.getAttribute('content')?.trim() || '';
          if (!productDescription && !/(?:tax included|shipping calculated)/i.test(metaDescription)) {
            productDescription = metaDescription;
          }
          if (productDescription && gridElement instanceof HTMLElement) {
            gridElement.dataset.quickAddDescription = productDescription;
          }
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    const modalContent = dialogComponent.querySelector('.quick-add-modal__content');
    modalContent?.scrollTo({ top: 0, behavior: 'instant' });
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const fallbackDescription = productGrid instanceof HTMLElement ? productGrid.dataset.quickAddDescription : '';
    morph(modalContent, productGrid);
    const promoScript = modalContent.querySelector('script[src*="/ruvine-product-promos.js"]');
    if (promoScript?.src && !customElements.get('rv-product-promos')) {
      try {
        await import(promoScript.src);
      } catch (error) {
        console.error('[Quick View] Unable to load product promotions.', error);
      }
    }

    // Keep Quick View copy consistent with the product card. Product pages can
    // contain longer editorial copy that is useful on-page but too heavy for
    // the compact drawer.
    const card = this.closest('.rp-mle-card');
    const cardTitle = card?.querySelector('.rp-mle-card__title')?.textContent?.trim();
    const cardDescription = card?.querySelector('.rp-mle-card__subtitle')?.textContent?.trim();
    const productHandle = new URL(this.productPageUrl).pathname.split('/products/')[1]?.split('/')[0] || '';
    if (productHandle) modalContent.setAttribute('data-product-handle', productHandle);
    const curatedCardDescriptions = {
      'ruvine-tumbler-full-set': 'Keeps you hydrated on the go.',
      'pretty-club-tote': 'Roomy canvas tote for everyday essentials.',
      'ruvine-key-chain-brown': 'A cute charm to dress up your tumbler.',
      'ruvine-hair-scrunchie': 'Cute everyday hair accessory.',
      'ruvine-eyelash-curler': 'For a natural, wide-eyed curl.',
    };
    const curatedDescription = curatedCardDescriptions[productHandle] || '';
    const modalTitle = modalContent.querySelector('.view-product-title a, .rv-pi-title');
    let modalDescription = modalContent.querySelector('.rv-pi-desc, .product-details .text-block.rte');
    if (!modalDescription) {
      modalDescription = Array.from(
        modalContent.querySelectorAll('.product-details .text-block, .product-details .rte, .product-details [class*="description"]')
      ).find((element) => {
        const copy = element.textContent?.trim() || '';
        const titleCopy = modalTitle?.textContent?.trim() || cardTitle || '';
        return (
          copy.length >= 12 &&
          copy.length <= 180 &&
          copy !== titleCopy &&
          !/tax included.*shipping calculated at checkout/i.test(copy) &&
          !/(?:sale|regular)\s+price|^RM\s*[\d,.]+$/i.test(copy)
        );
      });
    }
    const originalDescription = modalDescription?.textContent?.trim();

    if (cardTitle && modalTitle) modalTitle.textContent = cardTitle;
    if (cardDescription && modalDescription) modalDescription.textContent = cardDescription;

    // Product templates arrange their title/description in different nested
    // layout blocks. Build one shared summary outside those blocks so every
    // Quick View has identical centered geometry.
    const productDetails = modalContent.querySelector('.product-details');
    if (productDetails) {
      const summary = document.createElement('div');
      summary.className = 'quick-add-unified-summary';

      const summaryTitle = document.createElement('h2');
      summaryTitle.className = 'quick-add-unified-title';
      summaryTitle.textContent = cardTitle || modalTitle?.textContent?.trim() || '';
      summary.append(summaryTitle);

      const safeFallbackDescription = fallbackDescription && fallbackDescription !== summaryTitle.textContent
        ? fallbackDescription
        : '';
      const summaryCopy = cardDescription || curatedDescription || originalDescription || safeFallbackDescription;
      if (summaryCopy) {
        const summaryDescription = document.createElement('p');
        summaryDescription.className = 'quick-add-unified-description';
        summaryDescription.textContent = summaryCopy;
        summary.append(summaryDescription);
      }

      productDetails.prepend(summary);
      modalContent.querySelectorAll('.view-product-title, .rv-pi-desc, .quick-add-card-description').forEach((element) => {
        if (element !== summary) element.classList.add('quick-add-original-copy');
      });
      modalDescription?.classList.add('quick-add-original-copy');

      if (productHandle === 'sunday-kit') {
        productDetails
          .querySelectorAll('.text-block.rte, .product-description, [class*="description"]')
          .forEach((element) => {
            if (!element.closest('.quick-add-unified-summary')) element.classList.add('quick-add-original-copy');
          });
      }

      // Tax/shipping helper copy belongs on the full product page, not in the
      // compact Quick View (especially on mobile).
      productDetails.querySelectorAll('*').forEach((element) => {
        const copy = element.textContent?.replace(/\s+/g, ' ').trim() || '';
        if (
          element !== productDetails &&
          element.children.length <= 1 &&
          copy.length <= 120 &&
          /tax included.*shipping calculated at checkout/i.test(copy)
        ) {
          element.classList.add('quick-add-original-copy');
        }
      });
    }

    const cardPrice = card?.querySelector('.rp-mle-card__price')?.textContent?.trim();
    const rawDisplayedPrice = cardPrice || modalContent
      .querySelector('.rv-pi-price, product-price .price-item--sale, product-price .price, product-price')
      ?.textContent?.trim();
    const displayedPrice = rawDisplayedPrice?.match(/RM\s*[\d,.]+/)?.[0]?.replace(/\s+/g, '') || '';
    const drawerCtaText = modalContent.querySelector(
      '.rv-pi-cta .add-to-cart-text__content span span, .buy-buttons-block button .add-to-cart-text__content span span'
    );
    if (displayedPrice && drawerCtaText) drawerCtaText.textContent = `ADD TO BAG · ${displayedPrice}`;

    // Quick View needs one contained image, not the PDP gallery's inherited
    // aspect ratios and absolutely positioned slideshow children.
    const mediaRegion = modalContent.querySelector('.product-information__media');
    const sourceImage = mediaRegion?.querySelector('img');
    if (sourceImage) {
      const previewImage = sourceImage.cloneNode(true);
      previewImage.classList.add('quick-add-hero');
      previewImage.removeAttribute('id');
      mediaRegion.replaceChildren(previewImage);
    }

    // Ruvine's custom product templates use tier buttons instead of the native
    // variant picker. Wire the cloned buttons directly inside Quick View.
    modalContent.querySelectorAll('.rv-pi').forEach((root) => {
      const variantInput = root.querySelector('input[name="id"]');
      const price = root.querySelector('[data-rv-price]');
      const compare = root.querySelector('[data-rv-compare]');
      const ctaText = root.querySelector('.rv-pi-cta .add-to-cart-text__content span span');
      const buttons = Array.from(root.querySelectorAll('.rv-pi-tier-btn'));
      const lastCallActive = Date.now() <= Date.parse('2026-09-30T23:59:59+08:00');

      buttons.forEach((button) => {
        if (button.classList.contains('rv-pi-tier-card')) return;
        const label = button.querySelector('span:last-child');
        const match = label?.textContent?.trim().match(/^(.*?)(\s*\(\d+\s+Gifts?\))$/i);
        if (!label || !match) return;
        label.textContent = match[1].trim();
        const giftLine = document.createElement('small');
        giftLine.className = 'quick-add-gift-count';
        giftLine.textContent = match[2].trim();
        label.append(giftLine);
      });

      const initialButton = buttons.find((button) => button.dataset.variantId === variantInput?.value) || buttons[0];
      buttons.forEach((button) => {
        button.classList.toggle('is-selected', button === initialButton);
        button.setAttribute('aria-pressed', String(button === initialButton));
      });

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled || !variantInput) return;
          variantInput.value = button.dataset.variantId || '';
          buttons.forEach((item) => {
            item.classList.toggle('is-selected', item === button);
            item.setAttribute('aria-pressed', String(item === button));
          });

          const basePriceCents = Number(button.dataset.price || 0);
          const displayPriceCents = lastCallActive
            ? Math.floor((basePriceCents * 91) / 10000) * 100
            : basePriceCents;
          const value = displayPriceCents / 100;
          const compareValue = lastCallActive
            ? basePriceCents / 100
            : Number(button.dataset.compare || 0) / 100;
          const formattedPrice = new Intl.NumberFormat('en-MY', {
            style: 'currency',
            currency: 'MYR',
            minimumFractionDigits: value % 1 ? 2 : 0,
          }).format(value).replace('MYR', 'RM');

          if (price) price.textContent = formattedPrice;
          if (compare) {
            compare.textContent = compareValue > value
              ? new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' })
                  .format(compareValue)
                  .replace('MYR', 'RM')
              : '';
            compare.style.display = compareValue > value ? '' : 'none';
          }
          if (ctaText) ctaText.textContent = `ADD TO BAG · ${formattedPrice}`;

          // Custom tier buttons do not emit the theme's native variant media
          // event, so keep the Quick View hero image in sync explicitly.
          const imageUrl = button.dataset.imageUrl;
          if (imageUrl) {
            const heroImage = modalContent.querySelector('.product-information__media img');
            const heroPicture = heroImage?.closest('picture');

            heroPicture?.querySelectorAll('source').forEach((source) => source.removeAttribute('srcset'));
            if (heroImage instanceof HTMLImageElement) {
              heroImage.removeAttribute('srcset');
              heroImage.removeAttribute('sizes');
              heroImage.src = imageUrl;
            }
          }
        });
      });
    });

    // Put every template's purchase form in the same bottom row. Moving the
    // existing node preserves Shopify form behavior and selected variant IDs.
    const purchaseBar = modalContent.querySelector('.rv-pi-buy-buttons, .buy-buttons-block');
    if (purchaseBar) {
      purchaseBar.classList.add('quick-add-unified-purchase');
      modalContent.append(purchaseBar);
    }

    if (this.dataset.cartEditKey) {
      const quantity = this.dataset.cartEditQuantity || '1';
      modalContent.querySelectorAll('input[name="quantity"]').forEach((input) => {
        if (input instanceof HTMLInputElement) input.value = quantity;
      });
      modalContent.querySelectorAll('.add-to-cart-text__content span span, .rv-pi-cta .add-to-cart-text__content span span').forEach((label) => {
        label.textContent = 'UPDATE PRODUCT';
      });
      modalContent.querySelectorAll('button[type="submit"]').forEach((button) => {
        button.setAttribute('aria-label', 'Update product');
      });
    }

    this.#syncVariantSelection(modalContent);

    // Load the hero before revealing the drawer. This avoids the empty-image
    // flash caused by Shopify's lazy-loaded product media.
    const heroImage = modalContent.querySelector('.product-information__media img');
    if (heroImage instanceof HTMLImageElement) {
      heroImage.loading = 'eager';
      heroImage.fetchPriority = 'high';
      if (!heroImage.complete) {
        await Promise.race([
          heroImage.decode().catch(() => {}),
          new Promise((resolve) => window.setTimeout(resolve, 700)),
        ]);
      }
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('submit', this.#handleCartEditSubmit, { capture: true, signal: this.#abortController.signal });
  }

  #handleCartEditSubmit = async (event) => {
    const cartEditKey = this.dataset.cartEditKey;
    if (!cartEditKey || !(event.target instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const form = event.target;
    const submitButton = form.querySelector('button[type="submit"]');
    submitButton?.setAttribute('aria-busy', 'true');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    try {
      const formData = new FormData(form);
      const variantId = String(formData.get('id') || '');
      const quantity = Number(formData.get('quantity') || this.dataset.cartEditQuantity || 1);
      const originalVariantId = this.dataset.cartEditVariant || '';
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : '')
        .filter(Boolean)
        .join(',');

      if (!variantId) throw new Error('Please select a product option.');

      if (variantId === originalVariantId) {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: cartEditKey, quantity, sections: sectionIds }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to update product.');
        this.dispatchEvent(new CartAddEvent(result, variantId, {
          source: 'product-form-component',
          itemCount: quantity,
          sections: result.sections,
        }));
      } else {
        let properties = {};
        try { properties = JSON.parse(this.dataset.cartEditProperties || '{}'); } catch (_) { properties = {}; }
        const item = { id: Number(variantId), quantity, properties };
        const addResponse = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });
        const addResult = await addResponse.json();
        if (!addResponse.ok || addResult.status) throw new Error(addResult.description || addResult.message || 'Unable to update product.');

        const removeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: cartEditKey, quantity: 0, sections: sectionIds }),
        });
        const result = await removeResponse.json();
        if (!removeResponse.ok || result.status) throw new Error(result.description || result.message || 'Unable to update product.');
        this.dispatchEvent(new CartAddEvent(result, variantId, {
          source: 'product-form-component',
          itemCount: quantity,
          sections: result.sections,
        }));
      }

      this.dataset.cartEditKey = '';
      this.closeDialog();
    } catch (error) {
      console.error(error);
      window.alert(error instanceof Error ? error.message : 'Unable to update product.');
    } finally {
      submitButton?.removeAttribute('aria-busy');
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
